// frontend/src/scenes/Login/login.jsx
import React, { useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { setAuth } from "../../lib/authApi";
import "./Login.css";

const LOGIN_ENDPOINT = "/api/auth/login";

function clearLegacySupabaseAuth() {
  try {
    Object.keys(localStorage)
      .filter((key) => key.startsWith("sb-") || key.includes("supabase"))
      .forEach((key) => localStorage.removeItem(key));
    Object.keys(sessionStorage)
      .filter((key) => key.startsWith("sb-") || key.includes("supabase"))
      .forEach((key) => sessionStorage.removeItem(key));
  } catch {}
}

function safeJsonParse(text) {
  try {
    return JSON.parse(text);
  } catch {
    return null;
  }
}

export default function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();

  const redirectTo = useMemo(() => {
    const fromState = location.state?.from?.pathname;
    return fromState || "/lab-results";
  }, [location.state]);

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const canSubmit = email.trim().length > 0 && password.length > 0 && !loading;

  async function onSubmit(e) {
    e.preventDefault();
    clearLegacySupabaseAuth();
    setError("");
    setLoading(true);

    try {
      const res = await fetch(LOGIN_ENDPOINT, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email: email.trim().toLowerCase(),
          password,
        }),
      });

      const raw = await res.text();
      const data = safeJsonParse(raw);

      if (!res.ok) {
        const msg =
          data?.error ||
          data?.message ||
          raw ||
          `Login failed (HTTP ${res.status})`;
        throw new Error(msg);
      }

      const token = data?.token || data?.access_token || data?.jwt;
      if (!token) throw new Error("Login succeeded but no token was returned.");

      // Store using same key as authApi.js expects
      clearLegacySupabaseAuth();
      setAuth(token, data?.user);

      navigate(redirectTo, { replace: true });
    } catch (err) {
      setError(err?.message || "Login failed.");
    } finally {
      setLoading(false);
    }
  }

  return (
    <main className="staff-login-page">
      <section className="staff-login-shell" aria-labelledby="staff-login-heading">
        <aside className="staff-login-brand" aria-label="Equilibrium Scientific">
          <a className="staff-login-wordmark" href="https://equilibriumsci.com" aria-label="Equilibrium Scientific home">
            Equilibrium <span>Scientific</span>
          </a>
          <div className="staff-login-brand-copy">
            <p className="staff-login-eyebrow">Equilibrium Scientific</p>
            <h1 id="staff-login-heading">Laboratory Staff Access</h1>
            <p>
              Secure sign-in for approved laboratory personnel. Access clinical
              operations and laboratory results with your staff credentials.
            </p>
          </div>
          <p className="staff-login-assurance">
            Protected clinical workspace for authorized staff only.
          </p>
        </aside>

        <div className="staff-login-card">
          <p className="staff-login-eyebrow">Staff sign in</p>
          <h2>Welcome back</h2>
          <p className="staff-login-intro">
            Enter your Equilibrium Scientific staff credentials to continue.
          </p>

          {error ? (
            <div className="staff-login-alert" role="alert">
              {error}
            </div>
          ) : null}

          <form className="staff-login-form" onSubmit={onSubmit}>
            <label>
              Work email address
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoComplete="email"
              />
            </label>

            <label>
              Password
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="current-password"
              />
            </label>

            <button type="submit" disabled={!canSubmit}>
              {loading ? "Signing in..." : "Sign in securely"}
            </button>
          </form>
        </div>
      </section>
    </main>
  );
}
